import { CommonModule, isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  NgModule,
  OnInit,
  PLATFORM_ID,
  TemplateRef,
  ViewChild
} from "@angular/core";
import { MobilePageMenuOffcanvasService } from "@core/services/mobile-page-menu-offcanvas.service";
import { MobilePageMenuService } from "@core/services/mobile-page-menu.service";
import { ModalService } from "@core/services/modal.service";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NgbModalModule, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { filter, take } from "rxjs/operators";

import { DescriptionModalWrapperComponent } from "../description-modal-wrapper/description-modal-wrapper.component";

@Component({
  selector: "astrobin-mobile-page-menu-offcanvas",
  template: `
    <ng-template #pageMenuOffcanvas let-offcanvas>
      <ng-container *ngIf="pageMenuConfig$ | async as config">
        <div [attr.data-menu-id]="getCurrentMenuId()" class="offcanvas-header">
          <h5 class="offcanvas-title">
            <ng-container *ngIf="config.titleTemplate" [ngTemplateOutlet]="config.titleTemplate"></ng-container>
          </h5>
          <div class="header-actions">
            <!-- Icons from the template if available -->
            <ng-container *ngIf="config.iconsTemplate">
              <ng-container [ngTemplateOutlet]="config.iconsTemplate"></ng-container>
            </ng-container>

            <!-- Info/description button if available -->
            <ng-container *ngIf="config.descriptionTemplate">
              <button
                (click)="openPageMenuDescription($event)"
                class="btn btn-link btn-no-block text-white p-1 me-2"
                type="button"
              >
                <fa-icon icon="info-circle"></fa-icon>
              </button>
            </ng-container>

            <!-- Close button -->
            <button (click)="offcanvas.dismiss()" class="btn-close text-reset" type="button"></button>
          </div>
        </div>
        <div class="offcanvas-body">
          <!-- Main content template -->
          <ng-container
            *ngIf="config.template"
            [ngTemplateOutlet]="config.template"
            [ngTemplateOutletContext]="config.templateContext"
          ></ng-container>
        </div>
      </ng-container>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MobilePageMenuOffcanvasComponent implements OnInit {
  @ViewChild("pageMenuOffcanvas", { static: true }) pageMenuOffcanvasTemplate: TemplateRef<any>;

  pageMenuConfig$ = this.mobilePageMenuService.getMenuConfig();

  // Flag to check if we're in a browser environment
  private readonly isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly mobilePageMenuOffcanvasService: MobilePageMenuOffcanvasService,
    private readonly mobilePageMenuService: MobilePageMenuService,
    private readonly modalService: ModalService,
    private readonly translateService: TranslateService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    // Only register the template if we're in a browser environment
    if (this.isBrowser) {
      // Register the template with the service
      this.mobilePageMenuOffcanvasService.registerPageMenuTemplate(this.pageMenuOffcanvasTemplate);
    }
  }

  /**
   * Get the current menu ID for debugging purposes
   */
  getCurrentMenuId(): string | null {
    return this.mobilePageMenuService.getCurrentMenuId();
  }

  /**
   * Open the description panel as a modal for the page menu
   */
  openPageMenuDescription(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Only open modals in browser environment
    if (!this.isBrowser) {
      return;
    }

    this.pageMenuConfig$
      .pipe(
        filter(() => this.isBrowser),
        take(1)
      )
      .subscribe(config => {
        if (!config || !config.descriptionTemplate) {
          return;
        }

        const modalRef: NgbModalRef = this.modalService.open(DescriptionModalWrapperComponent, {
          size: "lg",
          centered: true
        });
        const componentInstance: DescriptionModalWrapperComponent = modalRef.componentInstance;

        componentInstance.titleTemplate = config.titleTemplate || this.translateService.instant("About this page");
        componentInstance.contentTemplate = config.descriptionTemplate;
        componentInstance.context = config.templateContext;
      });
  }
}

@NgModule({
  declarations: [MobilePageMenuOffcanvasComponent],
  imports: [CommonModule, TranslateModule, FontAwesomeModule, NgbModalModule],
  exports: [MobilePageMenuOffcanvasComponent]
})
export class MobilePageMenuOffcanvasModule {}
